import { Link } from "react-router-dom";
import { 
  ArrowRight, FileText, Calculator, Receipt, Users, BarChart3, Shield,
  Upload, Eye, Clock, AlertTriangle, Download, Settings, Percent,
  CreditCard, Bell, Search, CheckCircle2, Lock, FileCheck, Palette,
  Bot, Activity, TrendingUp, ThumbsUp, Mail, Repeat, ListChecks
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Layout } from "@/components/layout/Layout";
import { CTASection } from "@/components/shared/CTASection";
import { SEO } from "@/components/seo/SEO";

const featureSections = [
  {
    icon: Bot,
    title: "Automação de faturas",
    description: "Robôs e IA para execução das etapas críticas da entrada de dados da operação, reduzindo esforço manual e melhorando o acompanhamento do processo",
    color: "from-blue-500 to-cyan-500",
    features: [
      { icon: Bot, text: "Download e processamento automático de faturas" },
      { icon: Mail, text: "Coleta via e-mail, portal da distribuidora ou upload manual" },
      { icon: AlertTriangle, text: "Gestão de status: quarentena, erros, campos desconhecidos" },
      { icon: Upload, text: "Inclusão e edição manual de dados quando necessário" },
      { icon: Eye, text: "Acompanhamento dos status de processamento" },
      { icon: Search, text: "Identificação rápida de erros para tomada de ação" },
    ],
  },
  {
    icon: Calculator,
    title: "Regras de aluguel e cálculo das cobranças",
    description: "Transforme suas regras de negócio em processos parametrizados, reduzindo dependência de controles paralelos e aumentando a consistência do cálculo",
    color: "from-violet-500 to-purple-500",
    features: [
      { icon: Settings, text: "Dezenas de regras de aluguel já cadastradas para diferentes casos de uso" },
      { icon: Calculator, text: "Cálculo automático dos valores" },
      { icon: FileText, text: "Modelos padrão de fatura" },
      { icon: Palette, text: "Customização visual da fatura com sua identidade visual" },
      { icon: Percent, text: "Fórmulas de cálculo complexas e personalizáveis" },
      { icon: Settings, text: "Opção de desenvolvimento personalizado sob demanda" },
    ],
  },
  {
    icon: FileCheck,
    title: "Auditoria de faturas",
    description: "Validação automática de dados das faturas da concessionária, agilizando a conferência e fortalecendo a confiabilidade dos dados",
    color: "from-amber-500 to-orange-500",
    features: [
      { icon: FileCheck, text: "Cálculos automáticos de validação" },
      { icon: BarChart3, text: "Comparação de saldo, injeção e compensado" },
      { icon: AlertTriangle, text: "Destaque claro de divergências encontradas" },
      { icon: Search, text: "Identificação de inconsistências para revisão junto à concessionária" },
      { icon: Eye, text: "Auditorias de saldo com detalhamento" },
      { icon: Clock, text: "Histórico de auditorias por período" },
    ],
  },
  {
    icon: ListChecks,
    title: "Gestão do ciclo de faturamento",
    description: "Visibilidade sobre a evolução do ciclo de faturamento da carteira, acompanhando UCs e UGs ao longo das etapas operacionais e financeiras",
    color: "from-indigo-500 to-blue-500",
    features: [
      { icon: ListChecks, text: "Gestão por status: cadastro, regras, processamento, cálculo, emissão, pagamento" },
      { icon: Search, text: "Filtros avançados por período, cliente, consórcio, geradora e outros" },
      { icon: Repeat, text: "Integração com ERP quando aplicável para apoio à gestão de pagamentos" },
      { icon: Eye, text: "Visão consolidada para reduzir dispersão de informação" },
    ],
  },
  {
    icon: Receipt,
    title: "Gestão financeira e cobrança",
    description: "Automatização de etapas do processo de cobrança com maior visibilidade sobre o status financeiro das suas unidades consumidoras",
    color: "from-emerald-500 to-teal-500",
    features: [
      { icon: CreditCard, text: "Emissão de boletos" },
      { icon: Search, text: "Consulta de status de pagamento" },
      { icon: Percent, text: "Extras e descontos" },
      { icon: Clock, text: "Prorrogação e baixa" },
      { icon: Mail, text: "Envio de e-mails com faturas e cobrança" },
      { icon: Bell, text: "Régua de cobrança personalizada e automatizada" },
    ],
  },
  {
    icon: Activity,
    title: "Inteligência de saldo e rateio",
    description: "Análise da saúde da carteira por meio de indicadores que ajudam a acompanhar saldo de crédito, consumo compensado, inadimplência e histórico de rateio",
    color: "from-cyan-500 to-teal-500",
    features: [
      { icon: Activity, text: "Visualizar histórico de saldo, consumo, compensação e inadimplência" },
      { icon: TrendingUp, text: "Identificar oportunidades de redução de estoque de saldo" },
      { icon: BarChart3, text: "Melhor uso dos créditos da carteira" },
      { icon: Eye, text: "Indicadores ISC e ICC com sistema de faróis" },
    ],
  },
  {
    icon: TrendingUp,
    title: "Área do investidor",
    description: "Amplie a transparência da operação para os investidores das suas geradoras, com acompanhamento estruturado das informações relevantes",
    color: "from-cyan-500 to-blue-500",
    features: [
      { icon: Receipt, text: "Cobranças e boletos vinculados à geradora" },
      { icon: BarChart3, text: "Histórico de injeção, saldo, consumo e aluguel" },
      { icon: CreditCard, text: "Status dos boletos" },
      { icon: TrendingUp, text: "Indicadores de rendimento da usina" },
    ],
  },
  {
    icon: Users,
    title: "Área do cliente (consumidor)",
    description: "Ofereça uma experiência mais transparente para seu cliente consumidor, reduzindo atritos operacionais no atendimento",
    color: "from-pink-500 to-rose-500",
    features: [
      { icon: Download, text: "Acesso a 2ª via de boletos" },
      { icon: Eye, text: "Visualização de status financeiro e histórico de aluguel" },
      { icon: TrendingUp, text: "Economia com GD" },
      { icon: Bell, text: "Configuração de notificações" },
      { icon: Users, text: "Dados cadastrais do consumidor" },
    ],
  },
  {
    icon: Shield,
    title: "Permissões e auditoria",
    description: "Segurança, controle de acesso e rastreabilidade completa",
    color: "from-slate-600 to-slate-800",
    features: [
      { icon: Users, text: "Controle de acesso por perfil (RBAC)" },
      { icon: Lock, text: "Permissões granulares por usuário" },
      { icon: FileCheck, text: "Trilha de auditoria completa" },
      { icon: Clock, text: "Histórico de ações com timestamp e usuário" },
      { icon: Shield, text: "Isolamento de dados multi-tenant" },
      { icon: Download, text: "Exportação de logs para compliance" },
    ],
  },
  {
    icon: Palette,
    title: "White-label (opcional)",
    description: "Personalização visual para sua marca",
    color: "from-fuchsia-500 to-pink-500",
    features: [
      { icon: Palette, text: "Logo e cores personalizáveis" },
      { icon: FileText, text: "Domínio próprio para área do consumidor" },
      { icon: Bell, text: "E-mails com sua identidade visual" },
      { icon: FileText, text: "Documentos gerados com sua marca" },
    ],
    optional: true,
  },
];

export default function Funcionalidades() {
  return (
    <Layout>
      <SEO path="/funcionalidades" title="Funcionalidades — Cogni" description="Conheça as funcionalidades da plataforma Cogni para gestão de usinas, UCs, rateios, faturas e portal do consumidor." />
      {/* HERO */}
      <section className="gradient-hero py-14 sm:py-20 md:py-24 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[250px] sm:w-[400px] h-[250px] sm:h-[400px] bg-primary/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3" />
        <div className="absolute bottom-0 left-0 w-48 sm:w-64 h-48 sm:h-64 bg-accent/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/3" />
        <div className="container-wide relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <span className="inline-block px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-4 sm:mb-6">
              Recursos completos
            </span>
            <h1 className="text-display font-bold mb-4 sm:mb-6 text-balance">
              Nossas funcionalidades
            </h1>
            <p className="text-body-lg text-muted-foreground mb-6 sm:mb-8">
              Tudo o que você precisa para estruturar e automatizar o núcleo operacional da gestão de créditos de energia em um só lugar.
            </p>
            <Link to="/contato">
              <Button size="lg" className="gradient-primary text-primary-foreground px-8 glow-primary touch-target">
                Agendar demo
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* NAVEGAÇÃO RÁPIDA */}
      <section className="section-padding-sm section-alt border-b border-border">
        <div className="container-wide">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-4 sm:mb-6">
              <h2 className="text-base sm:text-lg font-semibold mb-2">Navegação rápida</h2>
              <p className="text-muted-foreground text-sm">
                Clique para ir direto à seção desejada.
              </p>
            </div>
            <div className="flex flex-wrap justify-center gap-2 sm:gap-3">
              {featureSections.map((section, index) => (
                <a
                  key={index}
                  href={`#${section.title.toLowerCase().replace(/\s/g, '-').replace(/[()]/g, '')}`}
                  className="flex items-center gap-2 bg-background border border-border rounded-full px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium hover:border-primary hover:text-primary transition-all hover:shadow-md touch-target"
                >
                  <div className={`w-4 h-4 sm:w-5 sm:h-5 rounded-md bg-gradient-to-br ${section.color} flex items-center justify-center`}>
                    <section.icon className="h-2.5 w-2.5 sm:h-3 sm:w-3 text-white" />
                  </div>
                  {section.title}
                </a>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* MATRIZ DE FUNCIONALIDADES */}
      <section className="section-padding">
        <div className="container-wide">
          <div className="space-y-14 sm:space-y-20">
            {featureSections.map((section, sectionIndex) => (
              <div 
                key={sectionIndex} 
                className="scroll-mt-20 sm:scroll-mt-24" 
                id={section.title.toLowerCase().replace(/\s/g, '-').replace(/[()]/g, '')}
              >
                <div className="flex items-start gap-3 sm:gap-4 mb-6 sm:mb-8">
                  <div className={`w-11 h-11 sm:w-14 sm:h-14 rounded-xl sm:rounded-2xl bg-gradient-to-br ${section.color} flex items-center justify-center flex-shrink-0 shadow-lg`}>
                    <section.icon className="h-5 w-5 sm:h-7 sm:w-7 text-white" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
                      <h2 className="text-xl sm:text-2xl md:text-3xl font-bold">{section.title}</h2>
                      {section.optional && (
                        <span className="text-xs bg-accent/50 text-accent-foreground px-2 sm:px-3 py-1 rounded-full font-medium">
                          Opcional
                        </span>
                      )}
                    </div>
                    <p className="text-muted-foreground mt-1 text-sm sm:text-base">{section.description}</p>
                  </div>
                </div>

                <div className={`rounded-xl sm:rounded-2xl p-4 sm:p-6 md:p-8 ${sectionIndex % 2 === 0 ? 'section-alt' : 'section-warm'}`}>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                    {section.features.map((feature, featureIndex) => (
                      <div 
                        key={featureIndex} 
                        className="flex items-start gap-2.5 sm:gap-3 bg-background rounded-lg sm:rounded-xl p-3 sm:p-4 border border-border shadow-sm hover:shadow-md transition-shadow"
                      >
                        <div className={`w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-gradient-to-br ${section.color} flex items-center justify-center flex-shrink-0`}>
                          <feature.icon className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-white" />
                        </div>
                        <span className="text-sm pt-0.5 sm:pt-1">{feature.text}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* RESUMO */}
      <section className="section-padding section-gradient-blue relative overflow-hidden">
        <div className="absolute top-0 right-0 w-60 sm:w-80 h-60 sm:h-80 bg-white/5 rounded-full blur-3xl" />
        <div className="container-narrow text-center relative z-10">
          <h2 className="text-headline font-bold mb-4 sm:mb-6">
            Uma plataforma, toda a operação
          </h2>
          <p className="text-body-lg text-muted-foreground mb-6 sm:mb-8 max-w-2xl mx-auto">
            Mais do que executar tarefas, a plataforma cria uma base operacional sólida para que sua empresa acompanhe melhor a carteira, reduza riscos e tome decisões com mais segurança.
          </p>
          <div className="flex flex-col sm:flex-row flex-wrap justify-center gap-3 sm:gap-4">
            <div className="flex items-center gap-2 text-sm bg-background/60 backdrop-blur-sm rounded-full px-4 py-2.5">
              <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0" />
              <span>Robôs e IA para automação</span>
            </div>
            <div className="flex items-center gap-2 text-sm bg-background/60 backdrop-blur-sm rounded-full px-4 py-2.5">
              <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0" />
              <span>Auditoria e rastreabilidade</span>
            </div>
            <div className="flex items-center gap-2 text-sm bg-background/60 backdrop-blur-sm rounded-full px-4 py-2.5">
              <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0" />
              <span>Área do investidor e consumidor</span>
            </div>
          </div>
        </div>
      </section>

      {/* CTA FINAL */}
      <CTASection
        title="Quer ver essas funcionalidades na prática?"
        subtitle="Agende uma demonstração e explore a plataforma com o apoio do nosso time."
      />
    </Layout>
  );
}
